import React, {useEffect, useState, memo} from "react";
import PropTypes from "prop-types";
import {Badge, Box, ListItemText, TextField} from "@mui/material";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import dayjs from "dayjs";
import {useRexAITrustedAdvisorStyles} from "./useRexAITrustedAdvisorStyles";
import StyledTooltip from "~/src/components/tooltips/StyledTooltip";

const SessionItemText = ({session, hasBadge, isEditing, onRenameComplete}) => {
  const {classes} = useRexAITrustedAdvisorStyles();
  const [editValue, setEditValue] = useState("");

  useEffect(() => {
    if (isEditing) {
      setEditValue(session.sessionName || "");
    }
  }, [isEditing, session.sessionName]);

  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      const trimmed = editValue.trim();
      if (trimmed && trimmed.length <= 100) {
        onRenameComplete(session.sessionId, trimmed);
      } else {
        onRenameComplete(session.sessionId, null);
      }
    } else if (event.key === "Escape") {
      onRenameComplete(session.sessionId, null);
    }
  };

  return (
    <ListItemText
      primary={(
        <Box className={classes.sessionItemPrimary}>
          {hasBadge && (
            <Badge
              color="primary"
              variant="dot"
              className={classes.sessionBadge}
              id="session-updated-badge"
            />
          )}
          {Boolean(session.pinnedAt) && (
            <StyledTooltip title="Pinned session" placement="top">
              <PushPinIcon fontSize="small" color="primary" />
            </StyledTooltip>
          )}
          {(session.shared || session.hasActiveParticipants) && (
            <StyledTooltip title="Shared session" placement="top">
              <GroupOutlinedIcon fontSize="small" sx={{mr: 0.5}} />
            </StyledTooltip>
          )}
          {isEditing ?
            (
              <TextField
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onBlur={() => onRenameComplete(session.sessionId, null)}
                onKeyDown={handleKeyDown}
                onClick={(e) => e.stopPropagation()}
                autoFocus
                size="small"
                variant="standard"
                fullWidth
                InputProps={{id: "session-rename-input"}}
                className={classes.sessionRenameField}
              />
            ) :
            (
              <Box className={classes.sessionName} id="session-name">
                {session.sessionName || "New Conversation"}
              </Box>
            )}
        </Box>
      )}
      secondary={!isEditing && dayjs(session.lastActivityAt).fromNow()}
      primaryTypographyProps={{fontSize: "0.875rem"}}
      secondaryTypographyProps={{variant: "caption"}}
    />
  );
};

SessionItemText.propTypes = {
  session: PropTypes.object.isRequired,
  hasBadge: PropTypes.bool.isRequired,
  isEditing: PropTypes.bool.isRequired,
  onRenameComplete: PropTypes.func.isRequired,
};

export default memo(SessionItemText);
